import '@theme/anio-video-player';
import { setupAnioAccordion } from '@theme/anio-accordion';
import { setupAnioSwipe } from './anio-swipe.js';
import { CartAddEvent, CartErrorEvent, CartGroupedSections, CartUpdateEvent, ThemeEvents } from '@theme/events';

const bundleComponents = ['case', 'band', 'pouch'];

function anioAccessoryTierIsActive(enabled, startsAt, now = Date.now()) {
  if (!enabled) return false;
  const start = String(startsAt || '').trim();
  if (!start) return true;
  const timestamp = Date.parse(start);
  return Number.isFinite(timestamp) && now >= timestamp;
}

// A theme configurator selects existing SKUs. Properties alone cannot turn a
// watch into an inventory-backed bundle or set its checkout price.
function getAnioBundleSelection(watch, products, selected, components = bundleComponents, discountPercent = 0, accessoryTiers = false) {
  const validComponents = components.length > 0 && new Set(components).size === components.length && components.every(kind => bundleComponents.includes(kind));
  const variants = [watch, ...components.map(kind => products[kind]?.variants?.find(variant => String(variant.id) === String(selected[kind])))];
  const valid = validComponents && variants.every(variant => variant && Number.isSafeInteger(Number(variant.id)) && Number(variant.id) > 0 && Number.isSafeInteger(variant.price) && variant.price >= 0);
  const available = valid && variants.every(variant => variant.available === true);
  const basePrice = valid ? variants.reduce((sum, variant) => sum + variant.price, 0) : null;
  // Display-only setting: enable only after the same set discount is verified in Shopify.
  // A theme cannot apply a discount by changing a cart/add request's price.
  const configuredPercent = Number(discountPercent);
  const tierUnits = accessoryTiers && available ? components.length : 0;
  const rate = accessoryTiers ? Math.min(tierUnits, 4) * 10 : Number.isInteger(configuredPercent) && configuredPercent > 0 && configuredPercent <= 100 ? configuredPercent : 0;
  // Shopify applies the offer to eligible accessory lines; the watch is never
  // discounted. Verified Shopify BXGY truncates each line's discount to whole
  // cents; rounding to nearest would overstate savings for prices ending in .99.
  const accessoryAmount = tierUnits ? variants.slice(1).reduce((sum, variant) => sum + Math.floor(variant.price * rate / 100), 0) : 0;
  const price = valid ? accessoryTiers ? basePrice - accessoryAmount : Math.round(basePrice * (100 - rate) / 100) : null;
  const referencePrice = valid ? variants.reduce((sum, variant) => sum + (Number.isSafeInteger(variant.compare_at_price) && variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price), 0) : null;
  const comparePrice = accessoryTiers ? (referencePrice > price ? referencePrice : null) : rate > 0 ? (basePrice > price ? basePrice : null) : referencePrice > price ? referencePrice : null;
  return {
    available,
    price,
    basePrice,
    discountPercent: rate,
    comparePrice,
    savings: comparePrice > price ? comparePrice - price : null,
    accessoryDiscount: { units: tierUnits, rate: accessoryTiers ? rate : 0, amount: accessoryAmount },
    items: available ? variants.map(variant => ({ id: Number(variant.id), quantity: 1 })) : [],
  };
}

class AnioProduct extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.classList.add('is-enhanced');
    const { signal } = this.controller;
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.currentVariant = this.variants.find(variant => String(variant.id) === this.dataset.selectedVariant)
      || this.variants.find(variant => variant.available) || this.variants[0];
    this.packageOptionIndex = Number(this.dataset.packageOptionIndex ?? -1);
    this.colourOptionIndex = Number(this.dataset.colourOptionIndex ?? 0);
    this.nativeProductForm = this.querySelector('product-form-component');
    this.nativeSubmitHandler = this.nativeProductForm?.getAttribute('on:submit') ?? this.nativeSubmitHandler;
    this.themeBundle = this.dataset.themeBundle === 'true' && this.packageOptionIndex < 0;
    this.bundleProducts = {};
    // Liquid declares the actual set contents for this theme. Missing products
    // never become fabricated SKUs, and unavailable selected variants still fail.
    this.bundleComponents = this.dataset.bundleComponents === undefined ? bundleComponents : this.dataset.bundleComponents.split(',').filter(Boolean);
    // Shopify supplies exact option values (including casing) and their order.
    this.querySelectorAll('[data-option-index]').forEach(input => {
      input.checked = input.value === this.currentVariant?.options[Number(input.dataset.optionIndex)];
    });
    this.galleryEntries = this.galleryEntries || [...this.querySelectorAll('[data-gallery-slide]')].map((slide, index) => ({
      slide,
      controls: [...this.querySelectorAll(`[data-gallery-index="${index}"]`)],
      colour: slide.dataset.galleryColour?.trim().toLowerCase(),
      variantIds: (slide.dataset.galleryVariantIds || '').split(',').filter(Boolean),
    }));
    this.gallery = this.galleryEntries.map(entry => entry.slide);
    this.galleryIndex = 0;
    if (this.querySelector('.ap-gallery')) setupAnioSwipe(this.querySelector('.ap-gallery'), {
      signal,
      onSwipe: direction => this.showImage(this.galleryIndex + direction),
      enabled: () => this.gallery.length > 1,
    });
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', event => {
        const entry = this.findEditorGalleryEntry(event);
        if (!entry) return;
        this.editorGalleryEntry = entry;
        this.updateGallery(this.activeVariant || this.currentVariant, this.isThemeBundleSelected());
      }, { signal });
      document.addEventListener('shopify:block:deselect', event => {
        if (!this.editorGalleryEntry || this.findEditorGalleryEntry(event) !== this.editorGalleryEntry) return;
        this.editorGalleryEntry = null;
        this.updateGallery(this.activeVariant || this.currentVariant, this.isThemeBundleSelected());
      }, { signal });
    }
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency: this.dataset.currency || 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 2 });
    if (this.themeBundle) this.initializeThemeBundle();
    this.addEventListener('change', event => {
      if (event.target.matches('[data-option-index]')) this.updateVariant();
      if (event.target.matches('[data-theme-package],[data-bundle-component]')) this.updateVariant();
      if (event.target.name === 'properties[Wechselarmband]') {
        const label = this.querySelector('[data-band-label]');
        if (label) label.textContent = event.target.value;
      }
    }, { signal });
    this.addEventListener('submit', event => this.submitThemeBundle(event), { signal, capture: true });
    this.addEventListener('click', event => {
      const thumbnail = event.target.closest('[data-gallery-index]');
      const arrow = event.target.closest('[data-gallery-direction]');
      if (thumbnail) this.showImage(Number(thumbnail.dataset.galleryIndex));
      if (arrow) this.showImage(this.galleryIndex + Number(arrow.dataset.galleryDirection));
    }, { signal });
    this.addEventListener('keydown', event => {
      if (!event.target.closest('.ap-thumbnails')) return;
      if (['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        this.showImage(this.galleryIndex + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1));
        this.querySelector(`.ap-thumbnails [data-gallery-index="${this.galleryIndex}"]:not([hidden])`)?.focus({ preventScroll: true });
      }
    }, { signal });
    setupAnioAccordion(this.querySelector('.ap-product-description'), signal);
    setupAnioAccordion(this.querySelector('.ap-band-disclosure'), signal);
    if (!this.nativeProductForm) {
      this.updateGallery(this.currentVariant, false);
      return;
    }
    this.deliveryText = this.querySelector('[data-availability]').dataset.delivery;
    this.updateVariant(false);
    this.scheduleAccessoryTierStart();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') return;
      this.updateVariant(false);
      this.scheduleAccessoryTierStart();
    }, { signal });
    if (this.themeBundle) this.querySelector('[data-theme-package-options]')?.removeAttribute('hidden');
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    clearTimeout(this.accessoryTierTimer);
    this.editorGalleryEntry = null;
    this.syncNativeSubmitHandler(false);
  }
  scheduleAccessoryTierStart() {
    clearTimeout(this.accessoryTierTimer);
    if (this.dataset.accessoryTierDiscounts !== 'true') return;
    const start = Date.parse(this.dataset.accessoryTierStartsAt || '');
    const remaining = start - Date.now();
    if (!Number.isFinite(remaining) || remaining <= 0) return;
    this.accessoryTierTimer = setTimeout(() => {
      this.updateVariant(false);
      this.scheduleAccessoryTierStart();
    }, Math.min(remaining + 50, 2147483647));
  }
  findEditorGalleryEntry(event) {
    if (!window.Shopify?.designMode) return null;
    const sectionId = event.detail?.sectionId;
    if (sectionId && this.closest('.shopify-section')?.id !== `shopify-section-${sectionId}`) return null;
    const entries = this.galleryEntries.filter(entry => entry.slide.hasAttribute('data-shopify-editor-block'));
    const target = entries.find(entry => entry.slide === event.target || entry.slide.contains(event.target));
    if (target) return target;
    const blockId = event.detail?.blockId;
    if (!blockId) return null;
    return entries.find(entry => {
      try { return JSON.parse(entry.slide.getAttribute('data-shopify-editor-block')).id === blockId; }
      catch { return false; }
    }) || null;
  }
  syncNativeSubmitHandler(isThemeBundle) {
    // The shared declarative listener captures at document, before this
    // element's submit listener. Remove only our form's delegation while the
    // bundle handler owns submission, then restore it for ordinary purchases.
    if (isThemeBundle) this.nativeProductForm?.removeAttribute('on:submit');
    else if (this.nativeSubmitHandler != null) this.nativeProductForm?.setAttribute('on:submit', this.nativeSubmitHandler);
  }
  initializeThemeBundle() {
    try {
      this.bundleProducts = JSON.parse(this.querySelector('[data-bundle-products]').textContent);
      if (!this.bundleComponents.length || new Set(this.bundleComponents).size !== this.bundleComponents.length || !this.bundleComponents.every(kind => bundleComponents.includes(kind) && Array.isArray(this.bundleProducts[kind]?.variants))) throw new Error('Incomplete bundle products');
      const url = new URL(window.location.href);
      this.querySelectorAll('[data-theme-package]').forEach(input => { input.checked = input.value === (url.searchParams.get('anio_package') === 'bundle' ? 'bundle' : 'single'); });
      this.bundleComponents.forEach(kind => {
        const controls = [...this.querySelectorAll(`[data-bundle-component="${kind}"]`)];
        const available = controls.filter(control => !control.disabled && this.bundleProducts[kind].variants.some(variant => variant.available === true && String(variant.id) === control.value));
        const fromUrl = url.searchParams.get(`anio_${kind}`);
        const selected = available.find(control => control.value === fromUrl) || available.find(control => control.checked) || available[0];
        controls.forEach(control => { control.checked = control === selected; });
      });
    } catch {
      // Progressive fallback remains a normal watch purchase if the theme's
      // optional product configuration cannot be read.
      this.themeBundle = false;
      this.bundleProducts = {};
    }
  }
  isThemeBundleSelected() {
    return this.themeBundle && this.querySelector('[data-theme-package]:checked')?.value === 'bundle';
  }
  getThemeBundleSelection(watch = this.activeVariant) {
    const selected = Object.fromEntries(this.bundleComponents.map(kind => [kind, this.querySelector(`[data-bundle-component="${kind}"]:checked`)?.value]));
    const tierMode = this.dataset.accessoryTierDiscounts === 'true';
    const tiersActive = anioAccessoryTierIsActive(tierMode, this.dataset.accessoryTierStartsAt);
    // A scheduled tier offer must not fall back to a legacy whole-set discount.
    return getAnioBundleSelection(watch, this.bundleProducts, selected, this.bundleComponents, tierMode ? 0 : this.dataset.discountPercent, tiersActive);
  }
  setCartError(message = '') {
    const error = this.querySelector('[ref="addToCartTextError"]');
    if (error) {
      error.textContent = message;
      error.classList.toggle('hidden', !message);
    }
    const liveRegion = this.querySelector('[ref="liveRegion"]');
    if (liveRegion) liveRegion.textContent = message;
  }
  async submitThemeBundle(event) {
    const form = this.querySelector('form');
    if (!this.isThemeBundleSelected() || event.target !== form) return;
    // Native document delegation was disabled when this bundle was selected.
    // Prevent the browser fallback and handle only the configured bundle here.
    event.preventDefault();
    event.stopPropagation();
    // A single-watch request can still be pending when the customer switches
    // the package selector. Its native controller owns that in-flight submit.
    if (this.bundleSubmitting || this.nativeProductForm?.getAttribute('aria-busy') === 'true') return;
    const selection = this.getThemeBundleSelection();
    if (!selection.available) {
      this.setCartError((this.dataset.copyErrorBundleSelection || 'Bitte wähle für jedes Zubehör eine verfügbare Variante.'));
      return;
    }
    this.bundleSubmitting = true;
    this.setCartError();
    const button = form.querySelector('[type="submit"]');
    const loading = this.querySelector('add-to-cart-component');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    loading?.addLoading?.();
    const sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));
    const sectionIds = [...new Set(sections)];
    const root = window.Shopify?.routes?.root || '/';
    const sourceId = form.id || this.id;
    const productId = this.querySelector('product-form-component')?.dataset.productId;
    let added = false;
    let result;
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: selection.items, sections: sectionIds, sections_url: window.location.pathname + window.location.search }),
      });
      result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || (this.dataset.copyErrorBundleAdd || 'Dein Set konnte nicht hinzugefügt werden. Bitte prüfe deine Auswahl.'));
      added = true;
      // The multi-add response contains only newly added lines. Native cart
      // listeners require the full cart, including any previously added items.
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('Cart refresh failed');
      const cart = await cartResponse.json();
      if (!Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) throw new Error('Invalid cart response');
      this.dispatchEvent(new CartAddEvent(cart, sourceId, {
        source: 'anio-pdp-configurator',
        productId,
        variantId: String(selection.items[0].id),
        itemCount: selection.items.reduce((sum, item) => sum + item.quantity, 0),
        sections: result.sections,
      }));
      document.dispatchEvent(new CustomEvent(ThemeEvents.productAjaxAdded, { detail: { product: result } }));
      const liveRegion = this.querySelector('[ref="liveRegion"]');
      if (liveRegion) liveRegion.textContent = (this.dataset.copyBundleAdded || 'Dein Set wurde in den Warenkorb gelegt.');
    } catch (error) {
      if (added) {
        // The order selection was already added: show the cart instead of
        // offering a retry which could duplicate all four items.
        window.location.assign(`${root}cart`);
      } else {
        const message = error?.message || this.dataset.copyErrorBundleAdd || 'Dein Set konnte nicht hinzugefügt werden. Bitte versuche es erneut.';
        this.setCartError(message);
        this.dispatchEvent(new CartErrorEvent(sourceId, message));
        document.dispatchEvent(new CustomEvent(ThemeEvents.productAjaxError, { detail: { errorMessage: message } }));
        // Shopify may add an available quantity before returning a stock error.
        // Reconcile the cart once, without retrying the add request.
        try {
          const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (response.ok) {
            const cart = await response.json();
            if (Array.isArray(cart.items) && Number.isInteger(cart.item_count)) this.dispatchEvent(new CartUpdateEvent(cart, sourceId, { source: 'anio-pdp-configurator', productId, itemCount: cart.item_count, sections: result?.sections }));
          }
        } catch { /* Keep the original add error visible. */ }
      }
    } finally {
      this.bundleSubmitting = false;
      loading?.removeLoading?.();
      button.removeAttribute('aria-busy');
      this.updateVariant(false);
    }
  }
  setSelectionImage(kind, src, alt) {
    if (!src) return null;
    let entry = this.galleryEntries.find(item => item.kind === kind);
    if (!entry) {
      const slide = document.createElement('div');
      slide.className = 'ap-gallery-slide';
      slide.dataset.gallerySlide = '';
      slide.dataset.galleryKind = kind;
      slide.dataset.galleryPresentation = kind.startsWith('watch-') ? 'full' : 'cutout';
      slide.hidden = true;
      const frame = document.createElement('div');
      frame.className = 'ap-gallery-image ap-gallery-image--selection';
      const image = document.createElement('img');
      image.width = 1600;
      image.height = 1600;
      image.loading = 'lazy';
      frame.append(image);
      slide.append(frame);
      this.querySelector('.ap-gallery').append(slide);
      const controls = ['.ap-thumbnails', '.ap-gallery-dots'].map(selector => {
        const button = document.createElement('button');
        button.type = 'button';
        button.hidden = true;
        button.dataset.galleryKind = kind;
        if (selector === '.ap-thumbnails') {
          const thumbnail = image.cloneNode();
          thumbnail.width = 240;
          thumbnail.height = 240;
          button.append(thumbnail);
        }
        this.querySelector(selector).append(button);
        return button;
      });
      entry = { slide, controls, kind };
      this.galleryEntries.push(entry);
    }
    const changed = entry.src !== src;
    entry.src = src;
    entry.slide.querySelector('img').alt = alt;
    if (changed) {
      entry.slide.querySelector('img').src = src;
      entry.controls.forEach(control => {
        const image = control.querySelector('img');
        if (!image) return;
        // Variant URLs come from Liquid's image_url filter, so retain the
        // original asset while requesting a small thumbnail rendition.
        const url = new URL(src, window.location.href);
        if (url.searchParams.has('width')) url.searchParams.set('width', '240');
        image.src = url.href;
        image.alt = '';
      });
    }
    return { entry, changed };
  }
  updateGallery(variant, isThemeBundle) {
    if (!variant || !this.galleryEntries.length) return;
    const activeSlide = this.gallery[this.galleryIndex];
    const watchChanged = this.galleryVariantId !== variant.id;
    // Keep every available colour for this package/option combination. The
    // selected colour determines the active image, not the gallery's contents.
    const availableVariants = this.variants.filter(candidate =>
      (candidate.available || candidate.id === variant.id) &&
      candidate.options.every((value, index) => index === this.colourOptionIndex || value === variant.options[index]));
    const matchesVariant = (entry, candidate) =>
      (!entry.colour || entry.colour === String(candidate.options[this.colourOptionIndex] || '').trim().toLowerCase()) &&
      (!entry.variantIds?.length || entry.variantIds.includes(String(candidate.id)));
    const isVariantImage = entry => Boolean(entry.colour || entry.variantIds?.length);
    const eligible = this.galleryEntries.filter(entry => !entry.kind &&
      availableVariants.some(candidate => matchesVariant(entry, candidate)));
    // New catalog colours can use their assigned photo without requiring an
    // art block. Each variant needs its own entry so its siblings stay visible.
    const fallbackImages = [];
    availableVariants.forEach(candidate => {
      if (eligible.some(entry => isVariantImage(entry) && matchesVariant(entry, candidate)) || !candidate.featured_image?.src) return;
      const { entry } = this.setSelectionImage(`watch-${candidate.id}`, candidate.featured_image.src, `Anio 6 – ${candidate.title}`);
      entry.variantIds = [String(candidate.id)];
      fallbackImages.push(entry);
    });
    eligible.unshift(...fallbackImages);
    const selectedWatch = eligible.find(entry => isVariantImage(entry) && matchesVariant(entry, variant))?.slide || eligible[0]?.slide;
    let changedComponent;
    if (isThemeBundle) this.bundleComponents.forEach(kind => {
      const control = this.querySelector(`[data-bundle-component="${kind}"]:checked`);
      const result = this.setSelectionImage(kind, control?.dataset.gallerySrc, control?.dataset.galleryAlt || kind);
      if (!result) return;
      eligible.push(result.entry);
      if (result.changed && this.galleryPackageIsBundle) changedComponent = result.entry.slide;
    });
    // An editor selection previews even filtered artwork without selecting its
    // purchase variant or changing the customer's normal gallery filtering.
    const editorEntry = window.Shopify?.designMode ? this.editorGalleryEntry : null;
    if (editorEntry && !eligible.includes(editorEntry)) eligible.push(editorEntry);
    this.galleryEntries.forEach(entry => {
      const index = eligible.indexOf(entry);
      entry.slide.hidden = true;
      entry.controls.forEach(control => {
        control.hidden = index < 0;
        control.dataset.galleryIndex = String(index);
        control.setAttribute('aria-pressed', 'false');
        if (index >= 0) control.setAttribute('aria-label', (this.dataset.copyImageShow || '[bild] anzeigen').replaceAll('[bild]', entry.slide.querySelector('img')?.alt || (this.dataset.copyImageNumber || 'Produktbild [nummer]').replaceAll('[nummer]', String(index + 1))));
      });
    });
    // Catalog fallback images may precede authored slides. Keep the visible
    // controls in the same order as swipe and arrow navigation.
    eligible.forEach(entry => entry.controls.forEach(control => control.parentElement.append(control)));
    this.gallery = eligible.map(entry => entry.slide);
    const singleImage = this.gallery.length <= 1;
    this.querySelector('.ap-product-media').classList.toggle('ap-product-media--single', singleImage);
    this.querySelectorAll('[data-gallery-direction], .ap-gallery-dots').forEach(control => { control.hidden = singleImage; });
    this.querySelector('.ap-thumbnail-navigation').hidden = singleImage;
    this.galleryVariantId = variant.id;
    this.galleryPackageIsBundle = isThemeBundle;
    const nextSlide = editorEntry?.slide || (watchChanged ? selectedWatch : changedComponent || (this.gallery.includes(activeSlide) ? activeSlide : selectedWatch));
    this.showImage(Math.max(0, this.gallery.indexOf(nextSlide)));
  }
  updatePackagePreview(variant) {
    if (!this.themeBundle || !variant) return;
    const colour = String(variant.options[this.colourOptionIndex] || '').trim().toLowerCase();
    this.querySelectorAll('[data-package-image]').forEach(image => {
      const kind = image.dataset.packageImage;
      // Reuse the actual accessory selection, including URL-restored colours.
      // Watch variants may have no catalog image, so retain verified artwork.
      const src = kind === 'watch'
        ? (colour === 'blue' ? image.dataset.blueSrc : colour === 'hibiscus' ? image.dataset.hibiscusSrc : variant.featured_image?.src)
        : this.querySelector(`[data-bundle-component="${kind}"]:checked`)?.dataset.gallerySrc;
      image.hidden = !src;
      if (src && image.getAttribute('src') !== src) image.setAttribute('src', src);
    });
  }
  showImage(index) {
    if (!this.gallery.length) return;
    this.galleryIndex = (index + this.gallery.length) % this.gallery.length;
    this.gallery.forEach((slide, i) => { slide.hidden = i !== this.galleryIndex; });
    this.querySelectorAll('[data-gallery-index]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.galleryIndex) === this.galleryIndex)));
    const thumbnails = this.querySelector('.ap-thumbnails');
    const active = thumbnails?.querySelector(`[data-gallery-index="${this.galleryIndex}"]:not([hidden])`);
    const viewer = this.querySelector('anio6-mobile-gallery');
    if (viewer?.querySelector('dialog[open]')) viewer.syncImage?.();
    if (!thumbnails || !active) return;
    const track = thumbnails.getBoundingClientRect();
    const item = active.getBoundingClientRect();
    const leftEdge = track.left + thumbnails.clientLeft;
    const topEdge = track.top + thumbnails.clientTop;
    const rightEdge = leftEdge + thumbnails.clientWidth;
    const bottomEdge = topEdge + thumbnails.clientHeight;
    const left = item.left < leftEdge ? item.left - leftEdge : Math.max(0, item.right - rightEdge);
    const top = item.top < topEdge ? item.top - topEdge : Math.max(0, item.bottom - bottomEdge);
    // Move only the thumbnail strip; scrollIntoView would also move the page.
    if (left || top) thumbnails.scrollBy({ left, top, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }
  updateWatchColourAvailability(options) {
    this.querySelectorAll('[data-watch-colour]').forEach(input => {
      const available = this.variants.some(variant => variant.available === true &&
        variant.options[this.colourOptionIndex] === input.value &&
        variant.options.every((value, index) => index === this.colourOptionIndex || value === options[index]));
      const label = input.closest('label');
      const text = input.dataset.optionLabel || input.value;
      input.disabled = !available;
      input.setAttribute('aria-label', available ? text : (this.dataset.copyColourSoldOut || '[farbe] – ausverkauft').replaceAll('[farbe]', text));
      label?.setAttribute('title', available ? text : (this.dataset.copyColourSoldOut || '[farbe] – ausverkauft').replaceAll('[farbe]', text));
      const status = label?.querySelector('[data-watch-colour-status]');
      if (status) status.hidden = available;
    });
  }
  updateVariant(updateHistory = true) {
    this.editorGalleryEntry = null;
    const optionCount = this.currentVariant?.options.length || this.variants[0]?.options.length || 0;
    const options = Array.from({ length: optionCount }, (_, index) => {
      const controls = this.querySelectorAll(`[data-option-index="${index}"]`);
      return controls.length ? [...controls].find(control => control.checked)?.value : this.currentVariant?.options[index];
    });
    this.updateWatchColourAvailability(options);
    const variant = this.variants.find(item => item.options.every((option, index) => option === options[index]));
    this.activeVariant = variant;
    const form = this.querySelector('form');
    const input = form.querySelector('[name="id"]');
    const button = form.querySelector('[type="submit"]');
    const isThemeBundle = this.isThemeBundleSelected();
    this.syncNativeSubmitHandler(isThemeBundle);
    const selection = isThemeBundle ? this.getThemeBundleSelection(variant) : null;
    const available = isThemeBundle ? selection.available : variant?.available;
    const buttonText = available ? this.dataset.copyCartLabel || 'In den Warenkorb' : variant ? this.dataset.copySoldOutLabel || 'Ausverkauft' : this.dataset.copyUnavailableLabel || 'Nicht verfügbar';
    input.value = variant?.id || '';
    input.dispatchEvent(new Event('change', { bubbles: true }));
    button.disabled = !available || Boolean(this.bundleSubmitting);
    button.querySelectorAll('.add-to-cart-text__content').forEach(text => { text.textContent = buttonText; });
    button.setAttribute('aria-label', buttonText);
    const packageValue = this.packageOptionIndex >= 0 ? options[this.packageOptionIndex] : '';
    const isBundle = Boolean(variant && String(packageValue || '').toLowerCase() === 'sorglos-bundle');
    this.querySelectorAll('[data-bundle-options]').forEach(fieldset => { fieldset.hidden = !isBundle; fieldset.disabled = !isBundle; });
    this.querySelectorAll('[data-theme-bundle-options]').forEach(fieldset => { fieldset.hidden = !isThemeBundle; fieldset.disabled = !isThemeBundle; });
    this.querySelectorAll('[data-bundle-price-note]').forEach(note => { note.hidden = !isThemeBundle || selection.discountPercent > 0; });
    const paymentTerms = this.querySelector('.ap-payment-terms');
    if (paymentTerms) paymentTerms.hidden = isThemeBundle;
    if (this.themeBundle) this.bundleComponents.forEach(kind => {
      const control = this.querySelector(`[data-bundle-component="${kind}"]:checked`);
      const selected = this.bundleProducts[kind]?.variants.find(item => String(item.id) === control?.value);
      this.querySelectorAll(`[data-bundle-label="${kind}"]${kind === 'band' ? ',[data-band-label]' : ''}`).forEach(label => { label.textContent = selected?.title || (this.dataset.copyChoose || 'Bitte wählen'); });
    });
    const pouch = this.querySelector('[data-bundle-pouch]');
    if (pouch) {
      pouch.disabled = !isBundle;
      pouch.value = options[this.colourOptionIndex] || '';
    }
    const packageLabel = this.querySelector('[data-package-label]');
    packageLabel.textContent = isThemeBundle ? this.dataset.themeBundleTitle || 'Dein Anio Set' : packageValue || packageLabel.dataset.defaultLabel || 'Anio 6';
    this.querySelector('[data-package-description]').textContent = isThemeBundle ? this.dataset.themeBundleDescription || 'Uhr + Zubehör' : isBundle ? this.dataset.copyBundleLabel || 'Uhr + Zubehör' : this.dataset.copySingleSummary || 'Deine Anio 6 mit Ladekabel';
    this.querySelector('[data-availability]').textContent = available ? this.deliveryText : isThemeBundle ? this.dataset.copyBundleUnavailableMessage || 'Eine Variante deines Sets ist aktuell nicht verfügbar' : this.dataset.copyUnavailableMessage || 'Diese Variante ist aktuell nicht verfügbar';
    if (!variant) return;
    this.currentVariant = variant;
    this.dataset.selectedVariant = String(variant.id);
    this.updateGallery(variant, isThemeBundle);
    this.updatePackagePreview(variant);
    const price = isThemeBundle ? selection.price : variant.price;
    this.querySelector('[data-price]').textContent = price === null ? '–' : this.money.format(price / 100);
    const mobilePrice = this.querySelector('[data-mobile-price]');
    if (mobilePrice) mobilePrice.textContent = price === null ? '–' : this.money.format(price / 100);
    const comparePrice = isThemeBundle ? selection.comparePrice : variant.compare_at_price;
    this.querySelectorAll('[data-compare-price], [data-mobile-compare-price]').forEach(compare => {
      compare.hidden = !(comparePrice > price);
      compare.textContent = compare.hidden ? '' : this.money.format(comparePrice / 100);
    });
    this.querySelectorAll('[data-bundle-savings]').forEach(label => {
      const savings = isThemeBundle && selection.available ? selection.savings : null;
      label.hidden = !(savings > 0);
      const tier = selection?.accessoryDiscount;
      label.textContent = label.hidden ? '' : (this.dataset.copySavingsLabel || 'Du sparst [betrag]').replaceAll('[betrag]', this.money.format(savings / 100)) + (tier?.amount > 0 ? (this.dataset.copyDiscountSuffix || ' · [prozent] % Zubehör-Rabatt').replaceAll('[prozent]', String(tier.rate)) : '');
    });
    if (updateHistory) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      if (this.themeBundle) {
        if (isThemeBundle) url.searchParams.set('anio_package', 'bundle');
        else url.searchParams.delete('anio_package');
        bundleComponents.forEach(kind => {
          const value = this.querySelector(`[data-bundle-component="${kind}"]:checked`)?.value;
          if (isThemeBundle && this.bundleComponents.includes(kind) && value) url.searchParams.set(`anio_${kind}`, value);
          else url.searchParams.delete(`anio_${kind}`);
        });
      }
      window.history.replaceState({}, '', url);
    }
  }
}

class AnioCarousel extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('.ap-track');
    this.previous = this.querySelector('[data-previous]');
    this.next = this.querySelector('[data-next]');
    this.progress = this.querySelector('.ap-progress > span');
    if (!this.track) return;
    this.track.setAttribute('tabindex', '0');
    this.track.setAttribute('aria-label', this.dataset.copyCarouselLabel || 'Karten – mit den Pfeiltasten navigieren');
    this.previous?.addEventListener('click', () => this.move(-1), { signal });
    this.next?.addEventListener('click', () => this.move(1), { signal });
    this.track.addEventListener('scroll', () => this.update(), { signal, passive: true });
    this.track.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.move(['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1); }
    }, { signal });
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);
    this.update();
  }
  disconnectedCallback() { this.controller?.abort(); this.resizeObserver?.disconnect(); this.controller = null; }
  move(direction) {
    const width = this.track.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap || 0);
    this.track.scrollBy({ left: width * direction, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }
  update() {
    const remaining = this.track.scrollWidth - this.track.clientWidth;
    if (this.previous) this.previous.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= remaining - 2;
    if (this.progress) this.progress.style.transform = `translateX(${remaining > 0 ? this.track.scrollLeft / remaining * 200 : 0}%)`;
  }
}

if (!customElements.get('anio-product')) customElements.define('anio-product', AnioProduct);
if (!customElements.get('anio-carousel')) customElements.define('anio-carousel', AnioCarousel);
